import React, { useEffect } from 'react'
import { signInRedirectCallback, signInRedirect, getUser } from '../services/auth.service'
import { useHistory } from 'react-router-dom'
import LoadingPage from '../components/loadingPage'
import { useDispatch } from 'react-redux'
import { storeUser } from '../actions/auth.actions'
import authLogger from '../utils/authLogger'

const SignInCallback = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  useEffect(() => {
    const handleCallback = async () => {
      authLogger.info('SignInCallback', 'Processing sign-in callback', {
        url: window.location.href,
        hasSearch: !!window.location.search,
      })

      try {
        const params = new URLSearchParams(window.location.search)
        const hasCode = params.has('code')
        const hasError = params.has('error')

        authLogger.info('SignInCallback', 'URL parameters', { hasCode, hasError })

        if (hasError) {
          const error = params.get('error')
          const errorDescription = params.get('error_description')
          authLogger.error('SignInCallback', 'OAuth error in URL', { error, errorDescription })
        }

        if (!hasCode && !hasError) {
          authLogger.info('SignInCallback', 'No code or error - checking for existing user')
          const existingUser = await getUser()

          if (existingUser) {
            authLogger.info('SignInCallback', 'Existing user found - redirecting to home')
            authLogger.logUserState('SignInCallback', existingUser)
            sessionStorage.removeItem('signInRedirectAttempt')
            history.push('/')
            return
          }

          const lastAttempt = localStorage.getItem('signInRedirectAttempt')
          const now = Date.now()
          const timeSinceLastAttempt = lastAttempt ? now - parseInt(lastAttempt) : null

          authLogger.info('SignInCallback', 'Checking for redirect loop', {
            lastAttempt,
            timeSinceLastAttempt,
          })

          if (lastAttempt && timeSinceLastAttempt < 5000) {
            authLogger.error('SignInCallback', 'Redirect loop detected - redirecting to /oops', {
              timeSinceLastAttempt,
            })
            localStorage.removeItem('signInRedirectAttempt')
            history.replace('/oops')
            return
          }

          authLogger.info('SignInCallback', 'No existing user - initiating sign-in redirect')
          localStorage.setItem('signInRedirectAttempt', now.toString())
          await signInRedirect()
          return
        }

        // Valid callback flow - process it
        authLogger.info('SignInCallback', 'Processing valid callback with code')
        localStorage.removeItem('signInRedirectAttempt')
        sessionStorage.removeItem('signInRedirectAttempt')

        const user = await signInRedirectCallback()

        if (user) {
          authLogger.info('SignInCallback', 'User obtained from callback')
          authLogger.logUserState('SignInCallback', user)
          dispatch(storeUser(user))
        } else {
          authLogger.warn('SignInCallback', 'No user returned from callback')
        }

        window.history.replaceState(
          {},
          window.document.title,
          window.location.origin + window.location.pathname
        )

        const redirectUrl = user?.state?.postLoginRedirect || '/'
        authLogger.info('SignInCallback', 'Sign-in complete - redirecting', { redirectUrl })
        history.push(redirectUrl)
      } catch (err) {
        authLogger.error('SignInCallback', 'Sign-in callback error', {
          error: err.message,
          stack: err.stack,
        })

        if (err.message && err.message.includes('No matching state found in storage')) {
          authLogger.info('SignInCallback', 'Ignoring "No matching state" error (likely React StrictMode)')
          return
        }

        localStorage.removeItem('signInRedirectAttempt')
        sessionStorage.removeItem('signInRedirectAttempt')
        history.replace('/oops')
      }
    }

    handleCallback()
  }, [dispatch, history])

  return <LoadingPage text='Signing in' />
}


export default SignInCallback
